"use client";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/shadcn-components/ui/drawer";
import { Button } from "@/shadcn-components/ui/button";
import { Separator } from "@/shadcn-components/ui/separator";
import { ChartIcon, TradeIcon, CloseIcon } from "@/components/icons";
import type { VybeDealInternal } from "@/vybe-trader-client/trade-history/types";
import {
  VybeDealType,
  formatDealType,
  formatDirection,
  dealTypeBuySellClass,
} from "@/vybe-trader-client/trade-history/types";
import HistoryDetailsContent from "../HistoryDetailsContent";

interface DealDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  deal: VybeDealInternal | null;
  onChart: () => void;
  onTrade: () => void;
}

export default function DealDetailsDrawer({
  isOpen,
  onClose,
  deal,
  onChart,
  onTrade,
}: DealDetailsDrawerProps) {
  if (!deal) return null;

  const displayType = formatDealType(deal.type);
  const directionLabel = formatDirection(deal.direction);
  const isTradeDeal =
    deal.type === VybeDealType.DealBuy || deal.type === VybeDealType.DealSell;

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="max-h-[88vh] flex flex-col">
        <DrawerHeader className="pb-2 px-4 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <DrawerTitle className="text-xl font-medium text-foreground truncate">
                {deal.symbol}
              </DrawerTitle>
              <div className="text-sm">
                {isTradeDeal ? (
                  <>
                    <span className={dealTypeBuySellClass(deal.type)}>
                      {displayType}
                    </span>
                    <span className="text-muted-foreground">
                      , {directionLabel}
                    </span>
                  </>
                ) : (
                  <span className={dealTypeBuySellClass(deal.type)}>
                    {displayType}
                  </span>
                )}
              </div>
            </div>
            <span className="text-sm text-muted-foreground shrink-0 pt-0.5">
              #{deal.ticketNumber}
            </span>
          </div>
          <DrawerDescription className="sr-only">Deal details</DrawerDescription>
        </DrawerHeader>

        <div className="px-4 overflow-y-auto flex-1 min-h-0 pb-2">
          <HistoryDetailsContent kind="deal" data={deal} />
        </div>

        {isTradeDeal && (
          <>
            <Separator className="shrink-0" />
            <div className="px-4 py-3 grid grid-cols-2 gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={() => handleAction(onChart)}
                className="justify-center gap-2 h-11 text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
              >
                <ChartIcon size={18} />
                Chart
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction(onTrade)}
                className="justify-center gap-2 h-11 text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
              >
                <TradeIcon size={18} />
                Trade
              </Button>
            </div>
          </>
        )}

        <div className="px-4 pb-4 pt-1 shrink-0">
          <DrawerClose asChild>
            <Button variant="secondary" className="w-full h-11 text-base">
              <CloseIcon size={16} className="mr-2" />
              Close
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
